import { useEffect, useState, useCallback } from "react";
import { format, parseISO } from "date-fns";
import { id as localeId } from "date-fns/locale";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ScrollText, RefreshCw } from "lucide-react";

const AKSI_STYLE = {
  TAMBAH: "bg-emerald-50 text-emerald-700 border-emerald-200",
  EDIT: "bg-[#60C0D0]/15 text-[#267B84] border-[#60C0D0]/40",
  "EDIT KAMAR": "bg-amber-50 text-amber-700 border-amber-200",
  "EDIT ANTRIAN": "bg-[#60C0D0]/15 text-[#267B84] border-[#60C0D0]/40",
  "PASIEN DATANG": "bg-[#00B9AD]/10 text-[#007d75] border-[#00B9AD]/40",
  "BATAL PASIEN DATANG": "bg-orange-50 text-orange-700 border-orange-200",
  "EMAIL TERKIRIM": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "EMAIL GAGAL": "bg-red-50 text-red-700 border-red-200",
  "EMAIL DILEWATI": "bg-slate-100 text-slate-600 border-slate-200",
  VOID: "bg-red-50 text-red-700 border-red-200",
  HAPUS: "bg-red-100 text-red-800 border-red-300",
  EXPORT: "bg-slate-100 text-slate-600 border-slate-200",
};

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/audit-logs");
      setLogs(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const fmtWaktu = (w) => {
    try {
      return format(parseISO(w), "d MMM yyyy, HH:mm", { locale: localeId });
    } catch {
      return w;
    }
  };

  return (
    <div className="space-y-5" data-testid="audit-log-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Audit Log</h1>
          <p className="text-sm text-slate-500 mt-1 flex items-center gap-2">
            <ScrollText size={14} className="text-[#60C0D0]" /> Jejak aktivitas: siapa mengubah apa dan kapan — {logs.length} entri terakhir
          </p>
        </div>
        <Button variant="outline" size="sm" className="gap-1.5" onClick={fetchLogs} data-testid="audit-refresh-button">
          <RefreshCw size={14} /> Muat Ulang
        </Button>
      </div>

      <Card className="border-slate-200/80 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]" data-testid="audit-table">
            <thead className="bg-[#267B84] text-white">
              <tr>
                {["Waktu", "Pengguna", "Aksi", "Entitas", "Detail"].map((h) => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {loading ? (
                <tr><td colSpan={5} className="px-4 py-14 text-center text-slate-400">Memuat data...</td></tr>
              ) : logs.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-14 text-center text-slate-400">Belum ada aktivitas tercatat.</td></tr>
              ) : logs.map((l) => (
                <tr key={l.id} className="hover:bg-slate-50" data-testid={`audit-row-${l.id}`}>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap text-xs">{fmtWaktu(l.waktu)}</td>
                  <td className="px-4 py-3">
                    {l.sumber === "Pengajuan Publik" ? (
                      <>
                        <span className="inline-block px-1.5 py-0.5 rounded border text-[10px] font-semibold bg-[#CDDC29]/20 text-[#6b7413] border-[#CDDC29]/50"
                          data-testid={`audit-sumber-${l.id}`}>Pengajuan Publik</span>
                        <p className="font-medium text-slate-800 text-xs mt-1">{l.pengaju_nama}</p>
                        <p className="text-[11px] text-slate-400">{l.pengaju_hp} · IP {l.ip}</p>
                      </>
                    ) : (
                      <>
                        <p className="font-medium text-slate-800 text-xs">{l.user_nama}</p>
                        <p className="text-[11px] text-slate-400">{l.user_email}</p>
                      </>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 rounded border text-[11px] font-semibold ${AKSI_STYLE[l.aksi] || "bg-slate-100 text-slate-600 border-slate-200"}`}>
                      {l.aksi}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-xs">{l.entitas === "KSM" ? "Departemen" : l.entitas}</td>
                  <td className="px-4 py-3 text-slate-600 text-xs">{l.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
